import { Feedback, useMutationCreateFeedback } from "@apis:feedback/feedback";
import { SVGInternalIcon } from "@components:SVGInternalIcon";
import { Textarea } from "@components:Textarea";
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  Slider,
  Switch,
} from "@mui/material";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import { useToasterStoreActions } from "@stores:global";
import { isUUID } from "@utils:uuid";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const initialFeedback = {
  difficulty: "easy",
  isAnswerCorrect: true,
  areReferencesCorrect: true,
  comment: "",
} as Feedback["POST"]["RequestBody"];

const difficultyOptions = [
  {
    value: 0,
    label: "easy",
    star: 1,
    color: "green",
  },
  {
    value: 50,
    label: "medium",
    star: 2,
    color: "orange",
  },
  {
    value: 100,
    label: "hard",
    star: 3,
    color: "red",
  },
];

interface StarRatingProps {
  difficult: "easy" | "medium" | "hard";
}

const StarRating = ({ difficult }: StarRatingProps) => {
  const numberOfStars = difficultyOptions.find(
    (option) => option.label === difficult,
  )?.star as number;

  return (
    <Box
      sx={{
        color: difficultyOptions.find((option) => option.label === difficult)
          ?.color,
      }}
    >
      {[...Array(numberOfStars)].map((_, index) => (
        <SVGInternalIcon icon="starIcon" key={index} />
      ))}
    </Box>
  );
};

const Android12Switch = styled(Switch)(({ theme }) => ({
  padding: 8,
  "& .MuiSwitch-track": {
    borderRadius: 22 / 2,
    "&::before, &::after": {
      content: '""',
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      width: 16,
      height: 16,
    },
    "&::before": {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
        theme.palette.getContrastText(theme.palette.primary.main),
      )}" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/></svg>')`,
      left: 12,
    },
    "&::after": {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
        theme.palette.getContrastText(theme.palette.primary.main),
      )}" d="M19,13H5V11H19V13Z" /></svg>')`,
      right: 12,
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "none",
    width: 16,
    height: 16,
    margin: 2,
  },
}));

interface TalkFeedbackProps {
  messageId: string;
}

export const TalkFeedback = ({ messageId }: TalkFeedbackProps) => {
  const { t } = useTranslation();
  const difficultyMarks = difficultyOptions.map((option) => ({
    value: option.value,
    label: t(`chat.feedback.difficulty.${option.label}`),
  }));
  const [open, setOpen] = useState(false);
  const [isAnswerCorrect, setIsAnswerCorrect] = useState<boolean | undefined>(
    undefined,
  );

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setFeedback((prevState) => ({
      ...prevState,
      ...initialFeedback,
    }));
  };
  const [feedback, setFeedback] =
    React.useState<Feedback["POST"]["RequestBody"]>(initialFeedback);

  useEffect(() => {
    setFeedback((prevState) => ({
      ...prevState,
      messageId,
    }));
  }, [messageId]);

  const toastActions = useToasterStoreActions();

  const feedbackMutation = useMutationCreateFeedback();

  const handleSubmit = async () => {
    await toastActions.setToasterPromise(
      feedbackMutation.mutateAsync(feedback),
      {
        onPending: () => ({
          message: t("chat.feedback.pending"),
        }),
        onSuccess: () => ({
          message: t("chat.feedback.success"),
        }),
        onError: () => ({
          message: t("chat.feedback.error"),
        }),
      },
    );
    handleClose();
  };

  return (
    <Box
      sx={{
        textAlign: "end",
      }}
    >
      <Box>
        <Tooltip title={t("chat.feedback.good")}>
          <IconButton
            size="small"
            onClick={() => {
              setFeedback((prevState) => ({
                ...prevState,
                isAnswerCorrect: true,
              }));
              handleOpen();
              setIsAnswerCorrect(true);
            }}
            sx={{
              "&:hover": {
                "& .like-icon": {
                  fill: "green",
                },
              },
              color: isAnswerCorrect === true ? "green" : undefined,
            }}
            disabled={!isUUID(messageId)}
          >
            <SVGInternalIcon
              icon="likeIcon"
              sx={{
                height: 20,
                width: 20,
              }}
            />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("chat.feedback.bad")}>
          <IconButton
            size="small"
            onClick={() => {
              setFeedback((prevState) => ({
                ...prevState,
                isAnswerCorrect: false,
              }));
              handleOpen();
              setIsAnswerCorrect(false);
            }}
            sx={{
              "&:hover": {
                "& .dislike-icon": {
                  fill: "red",
                },
              },
              color: isAnswerCorrect === false ? "red" : undefined,
            }}
            disabled={!isUUID(messageId)}
          >
            <SVGInternalIcon
              icon="dislikeIcon"
              sx={{
                height: 20,
                width: 20,
              }}
            />
          </IconButton>
        </Tooltip>
      </Box>
      <Modal
        open={open}
        onClose={() => {
          handleClose();
          setIsAnswerCorrect(undefined);
        }}
        sx={{
          "& .MuiPaper-root": {
            padding: 0,
          },
        }}
      >
        <Card
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 700,
            boxShadow: 24,
            borderRadius: 5,
          }}
        >
          <CardHeader
            title={
              <Box display="flex" alignItems="center" gap={2}>
                <span>{t("chat.feedback.title")}</span>
                {isAnswerCorrect === true ? (
                  <SVGInternalIcon
                    icon="likeIcon"
                    sx={{
                      height: 20,
                      width: 20,
                      "& .like-icon": {
                        fill: "green",
                      },
                    }}
                  />
                ) : (
                  <SVGInternalIcon
                    icon="dislikeIcon"
                    sx={{
                      height: 20,
                      width: 20,
                      "& .dislike-icon": {
                        fill: "red",
                      },
                    }}
                  />
                )}
              </Box>
            }
            sx={{
              px: 3,
            }}
          />
          <Divider />
          <CardContent
            sx={{
              p: 3,
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
            }}
          >
            <Box>
              <Box display="flex" alignItems="center" gap={2}>
                <span>{t("chat.feedback.difficulty.label")}</span>
                <StarRating difficult={feedback.difficulty} />
              </Box>

              <Box
                sx={{
                  width: 500,
                  mx: "auto",
                  pt: 2,
                }}
              >
                <Slider
                  defaultValue={0}
                  onChange={(_e, value) => {
                    setFeedback((prevState) => ({
                      ...prevState,
                      difficulty: difficultyOptions.find(
                        (option) => option.value === value,
                      )?.label as "easy" | "medium" | "hard",
                    }));
                  }}
                  valueLabelFormat={(value) => {
                    const option = difficultyOptions.find(
                      (opt) => opt.value === value,
                    );
                    return option
                      ? t(`chat.feedback.difficulty.${option.label}`)
                      : "";
                  }}
                  step={null}
                  marks={difficultyMarks}
                />
              </Box>
            </Box>
            <Box
              sx={{
                pt: 2,
              }}
              display="flex"
              alignItems="center"
              gap={2}
            >
              <span>{t("chat.feedback.referencesQuestion")}</span>
              <Android12Switch
                data-cy="switch-references"
                checked={feedback.areReferencesCorrect}
                onChange={(e) => {
                  setFeedback((prevState) => ({
                    ...prevState,
                    areReferencesCorrect: e.target.checked,
                  }));
                }}
              />
            </Box>
            <Box
              sx={{
                pt: 2,
              }}
            >
              <Textarea
                value={feedback.comment || ""}
                onChange={(e) =>
                  setFeedback((prevState) => ({
                    ...prevState,
                    comment: e?.target?.value,
                  }))
                }
                minRows={4}
                placeholder={t("chat.feedback.commentPlaceholder")}
              />
            </Box>
          </CardContent>
          <CardActions
            sx={{
              justifyContent: "flex-end",
              p: 3,
              pt: 0,
            }}
          >
            <Button
              data-cy="button-feedback-submit"
              variant="contained"
              sx={{
                color: "white",
              }}
              onClick={async () => {
                await handleSubmit();
              }}
            >
              {t("chat.feedback.submit")}
            </Button>
          </CardActions>
        </Card>
      </Modal>
    </Box>
  );
};
